import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export type PendingApprovals = {
  partners: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    status: string;
    createdAt: string;
    requestedHospitals?: Array<{ id: string; name: string }>;
  }>;
  rateRequests: Array<{
    id: string;
    milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
    points: number;
    pendingPoints: number | null;
    partner: { firstName: string; lastName: string };
    hospital: { name: string } | null;
    requestedByStaff: { name: string } | null;
  }>;
  leadPointRequests: Array<{
    id: string;
    milestone: 'NEW' | 'OPD_VISITED' | 'IPD_ADMITTED';
    requestedPoints: number;
    partner: { firstName: string; lastName: string };
    hospital: { name: string };
    lead: { patientName: string; phone: string; id?: string };
    requestedByStaff: { name: string } | null;
  }>;
};

type Props = {
  mode: 'chain' | 'hospital';
  /** Chain admin: approve rate / lead-point requests */
  canApprove: boolean;
  /** Sub-admin or chain admin: approve new partner accounts */
  canApprovePartner?: boolean;
};

export default function PartnerApprovalsPanel({
  mode,
  canApprove,
  canApprovePartner = canApprove,
}: Props) {
  const qc = useQueryClient();
  const queryKey = mode === 'chain' ? ['chain-partner-approvals'] : ['hospital-partner-approvals'];
  const [rateRejectReasonById, setRateRejectReasonById] = useState<Record<string, string>>({});
  const [leadRejectReasonById, setLeadRejectReasonById] = useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res =
        mode === 'chain'
          ? await referralPartnerApi.admin.chain.pendingApprovals()
          : await referralPartnerApi.admin.hospital.pendingApprovals();
      return (res.data?.data ?? { partners: [], rateRequests: [], leadPointRequests: [] }) as PendingApprovals;
    },
  });

  const chainApi = referralPartnerApi.admin.chain;

  const approvePartnerMut = useMutation({
    mutationFn: (id: string) =>
      mode === 'chain'
        ? chainApi.approvePartner(id)
        : referralPartnerApi.admin.hospital.approvePartner(id),
    onSuccess: () => {
      toast.success('Partner approved');
      qc.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve partner'),
  });

  const approveRateMut = useMutation({
    mutationFn: (id: string) => chainApi.approveRate(id),
    onSuccess: () => {
      toast.success('Rate approved');
      qc.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve rate'),
  });

  const rejectRateMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => chainApi.rejectRate(id, reason),
    onSuccess: () => {
      toast.success('Rate rejected');
      qc.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to reject rate'),
  });

  const approveLeadPointsMut = useMutation({
    mutationFn: (id: string) => chainApi.approveLeadPoints(id),
    onSuccess: () => {
      toast.success('Lead points approved');
      qc.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to approve lead points'),
  });

  const rejectLeadPointsMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      chainApi.rejectLeadPoints(id, reason),
    onSuccess: () => {
      toast.success('Lead points rejected');
      qc.invalidateQueries({ queryKey });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to reject lead points'),
  });

  const partners = data?.partners ?? [];
  const rateRequests = data?.rateRequests ?? [];
  const leadPointRequests = data?.leadPointRequests ?? [];

  const summary = useMemo(
    () => ({
      partners: partners.length,
      rates: rateRequests.length,
      leads: leadPointRequests.length,
    }),
    [partners.length, rateRequests.length, leadPointRequests.length]
  );

  const scopeHint =
    mode === 'chain'
      ? 'Approve referral partners and point requests for hospitals in your chain.'
      : canApprove
        ? 'Review and approve partner-related requests for your hospital.'
        : 'Track requests awaiting chain admin approval for your hospital.';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Partner approvals</h1>
        <p className="text-gray-500">
          {scopeHint} Pending: {summary.partners} partners, {summary.rates} rate changes, {summary.leads}{' '}
          lead-wise requests.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {(error as any)?.message || 'Failed to load'}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <>
          {(mode === 'chain' || canApprovePartner) && (
            <section className="space-y-3">
              <h2 className="text-lg font-medium">Partner account approvals</h2>
              <div className="rounded-lg border overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-4 py-2">Name</th>
                      <th className="px-4 py-2">Email</th>
                      <th className="px-4 py-2">Phone</th>
                      <th className="px-4 py-2">Hospitals</th>
                      <th className="px-4 py-2">Requested</th>
                      {canApprovePartner && <th className="px-4 py-2">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {partners.map((p) => (
                      <tr key={p.id}>
                        <td className="px-4 py-2">
                          {p.firstName} {p.lastName}
                        </td>
                        <td className="px-4 py-2">{p.email}</td>
                        <td className="px-4 py-2">{p.phone}</td>
                        <td className="px-4 py-2 text-xs text-gray-600">
                          {(p.requestedHospitals ?? []).map((h) => h.name).join(', ') || '—'}
                        </td>
                        <td className="px-4 py-2">{new Date(p.createdAt).toLocaleString()}</td>
                        {canApprovePartner && (
                          <td className="px-4 py-2">
                            <Button
                              size="sm"
                              onClick={() => approvePartnerMut.mutate(p.id)}
                              disabled={approvePartnerMut.isPending}
                            >
                              Approve
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {partners.length === 0 && (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-gray-500"
                          colSpan={canApprovePartner ? 6 : 5}
                        >
                          No pending partner approvals
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-lg font-medium">
              Pending milestone rate changes {mode === 'chain' ? '(your hospitals)' : ''}
            </h2>
            <div className="rounded-lg border overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Partner</th>
                    <th className="px-4 py-2">Hospital</th>
                    <th className="px-4 py-2">Milestone</th>
                    <th className="px-4 py-2">Current</th>
                    <th className="px-4 py-2">Requested</th>
                    {canApprove && <th className="px-4 py-2">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rateRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2">
                        {r.partner.firstName} {r.partner.lastName}
                      </td>
                      <td className="px-4 py-2">{r.hospital?.name || '—'}</td>
                      <td className="px-4 py-2">{r.milestone}</td>
                      <td className="px-4 py-2">{r.points}</td>
                      <td className="px-4 py-2">{r.pendingPoints ?? '—'}</td>
                      {canApprove && (
                        <td className="px-4 py-2 space-y-2">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveRateMut.mutate(r.id)}
                              disabled={approveRateMut.isPending}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                rejectRateMut.mutate({ id: r.id, reason: rateRejectReasonById[r.id] })
                              }
                              disabled={rejectRateMut.isPending}
                            >
                              Reject
                            </Button>
                          </div>
                          <Input
                            placeholder="Rejection reason (optional)"
                            value={rateRejectReasonById[r.id] ?? ''}
                            onChange={(e) =>
                              setRateRejectReasonById((prev) => ({ ...prev, [r.id]: e.target.value }))
                            }
                            className="h-8"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                  {rateRequests.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={canApprove ? 6 : 5}>
                        No pending rate requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-medium">
              Pending lead-wise points requests {mode === 'chain' ? '(your hospitals)' : ''}
            </h2>
            <div className="rounded-lg border overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2">Lead</th>
                    <th className="px-4 py-2">Hospital</th>
                    <th className="px-4 py-2">Milestone</th>
                    <th className="px-4 py-2">Requested points</th>
                    {canApprove && <th className="px-4 py-2">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {leadPointRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{r.lead.patientName}</div>
                        <div className="text-xs text-gray-500">{r.lead.phone}</div>
                      </td>
                      <td className="px-4 py-2">{r.hospital.name}</td>
                      <td className="px-4 py-2">{r.milestone}</td>
                      <td className="px-4 py-2">{r.requestedPoints}</td>
                      {canApprove && (
                        <td className="px-4 py-2 space-y-2">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveLeadPointsMut.mutate(r.id)}
                              disabled={approveLeadPointsMut.isPending}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() =>
                                rejectLeadPointsMut.mutate({ id: r.id, reason: leadRejectReasonById[r.id] })
                              }
                              disabled={rejectLeadPointsMut.isPending}
                            >
                              Reject
                            </Button>
                          </div>
                          <Input
                            placeholder="Rejection reason (optional)"
                            value={leadRejectReasonById[r.id] ?? ''}
                            onChange={(e) =>
                              setLeadRejectReasonById((prev) => ({ ...prev, [r.id]: e.target.value }))
                            }
                            className="h-8"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                  {leadPointRequests.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={canApprove ? 5 : 4}>
                        No pending lead-wise point requests
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
